import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { Header, VStack } from 'tio-reimagined/components/layout';
import { eq } from 'tio-ui/utilities';
import {
  PhPath,
  PhBuildings,
  PhCaretDown,
  PhUserCircle,
  PhEyeSlash,
  PhEye,
  PhMoon,
  PhSun,
  PhSparkle,
  PhWarning,
  PhGraduationCap,
  PhChatCircleDots,
} from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import MegaMenuNav from 'tio-employee/components/reimagined/mega-menu-nav';
import DataSourceDocs from 'tio-employee/components/reimagined/data-source-docs';
import ReviewDrawer from 'tio-employee/components/reimagined/review-drawer';
import ToastContainer from 'tio-reimagined/components/toast';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type ComponentReviewService from 'tio-employee/services/component-review';
import type ReimaginedToastService from 'tio-employee/services/reimagined-toast';
import {
  DEMO_CLIENT_LIST,
  DEMO_SCENARIO_LIST,
  DEMO_TAS_SCENARIO_LIST,
  getDemoClientIdFromUrl,
  getDemoScenarioIdFromUrl,
  getCurrentDemoTasScenarioId,
  getDemoData,
  getDemoScenario,
  isReimaginedDemoMode,
  setDemoClientId,
  setDemoScenarioId,
  setDemoTasScenarioId,
  type DemoClientId,
  type DemoScenarioId,
  type DemoTasScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

type Intent = 'brand' | 'success' | 'warning';

interface ScenarioOption {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  employeeCount?: number;
  taUtilization?: number;
}

interface ScenarioPickerSignature {
  Args: {
    intent: Intent;
    label: string;
    title: string;
    subtitle: string;
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    options: ScenarioOption[];
    selectedId: string;
    isOpen: boolean;
    onToggle: () => void;
    onClose: () => void;
    onSelect: (id: string) => void;
    /** Render the option's industry / employee-count sub-line (client picker only). */
    showClientMeta?: boolean;
  };
}

/**
 * <ScenarioPicker> — the trigger pill + dropdown panel used three times in
 * the demo header (Client / User Scenario / TA Scenario). Same shape with
 * a different intent recipe + icon + option metadata. Co-located in this
 * file since it's a single-consumer extraction.
 */
class ScenarioPicker extends Component<ScenarioPickerSignature> {
  get rootClass() {
    // Intent modifier lives on the root so the --sel-* custom props cascade
    // into BOTH the trigger button and the (sibling) dropdown panel + rows.
    return `relative tio-demo-selector--${this.args.intent}`;
  }

  get optionActiveClass() {
    return `tio-demo-option-active`;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class={{this.rootClass}}>
      <button type="button" class="tio-demo-selector" {{on "click" @onToggle}}>
        <@icon @size={{20}} class="tio-demo-selector-meta" />
        <div class="text-left">
          <div class="text-sm font-semibold tio-demo-selector-heading">
            {{@title}}
          </div>
          <div class="text-xs tio-demo-selector-meta">{{@subtitle}}</div>
        </div>
        <PhCaretDown @size={{16}} class="tio-demo-selector-meta" />
      </button>

      {{#if @isOpen}}
        {{! Backdrop }}
        <button
          type="button"
          class="fixed inset-0 z-10"
          aria-label="Close menu"
          {{on "click" @onClose}}
        ></button>

        {{! Dropdown }}
        <div
          class="absolute right-0 mt-2 w-72 tio-bg-surface rounded-lg shadow-lg border tio-border-default z-20 overflow-hidden"
        >
          <div class="px-4 py-2 tio-bg-sunken border-b tio-border-default">
            <span
              class="text-xs font-semibold tio-fg-muted uppercase tracking-wide"
            >{{@label}}</span>
          </div>
          {{#each @options as |option|}}
            <button
              type="button"
              class="tio-demo-option w-full flex items-center gap-3 px-4 py-3 text-left
                {{if (eq option.id @selectedId) this.optionActiveClass}}"
              {{on "click" (fn @onSelect option.id)}}
            >
              <@icon @size={{24}} class="tio-fg-disabled shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="font-semibold tio-fg-primary">{{option.name}}</div>
                {{#if @showClientMeta}}
                  <div class="text-sm tio-fg-muted">{{option.industry}}</div>
                  <div class="text-xs tio-fg-disabled mt-1">
                    {{option.employeeCount}}
                    employees •
                    {{option.taUtilization}}% TA utilization
                  </div>
                {{else}}
                  <div class="text-sm tio-fg-muted">{{option.description}}</div>
                {{/if}}
              </div>
              {{#if (eq option.id @selectedId)}}
                <div class="tio-demo-option-dot"></div>
              {{/if}}
            </button>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

export default class ReimagainedLayout extends Component {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare componentReview: ComponentReviewService;
  @service('reimagined-toast') declare toast: ReimaginedToastService;

  @tracked selectedClientId: DemoClientId = getDemoClientIdFromUrl();
  @tracked selectedScenarioId: DemoScenarioId = getDemoScenarioIdFromUrl();
  @tracked selectedTasScenarioId: DemoTasScenarioId = getCurrentDemoTasScenarioId();
  @tracked isClientMenuOpen = false;
  @tracked isScenarioMenuOpen = false;
  @tracked isTasScenarioMenuOpen = false;
  @tracked isLegacyFrameHidden = this.initLegacyFrameState();
  @tracked isDarkMode = false;
  @tracked isDemoMode = this.getIsDemoMode();

  constructor(owner: Owner, args: object) {
    super(owner, args);
    // Apply legacy frame state on init
    if (this.isLegacyFrameHidden) {
      this.applyLegacyFrameState(true);
    }
  }

  private initLegacyFrameState() {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('reimagined-legacy-frame-hidden') === 'true';
  }

  private applyLegacyFrameState(hidden: boolean) {
    const legacyBanner = document.querySelector('[data-legacy-banner]');
    const legacySidebar = document.querySelector('[data-legacy-sidebar]');
    if (legacyBanner) {
      legacyBanner.classList.toggle('hidden', hidden);
    }
    if (legacySidebar) {
      legacySidebar.classList.toggle('hidden', hidden);
    }
    document.body.classList.toggle('reimagined-fullscreen', hidden);
  }

  get clientOptions(): ScenarioOption[] {
    return DEMO_CLIENT_LIST.map((c) => ({
      id: c.id,
      name: c.name,
      industry: c.industry,
      employeeCount: c.employeeCount,
      taUtilization: c.taUtilization,
    }));
  }

  get scenarioOptions(): ScenarioOption[] {
    return DEMO_SCENARIO_LIST.map((s) => ({
      id: s.id,
      name: s.name,
      description: s.description,
    }));
  }

  get tasScenarioOptions(): ScenarioOption[] {
    return DEMO_TAS_SCENARIO_LIST.map((id) => {
      switch (id) {
        case 'not-started':
          return { id, name: 'TA Not Started', description: 'No applications yet' };
        case 'in-progress':
          return { id, name: 'TA In Progress', description: 'Pending or active requests' };
        case 'approved':
          return { id, name: 'TA Approved', description: 'Approved, awaiting payment' };
        case 'rejected':
          return { id, name: 'TA Rejected', description: 'Declined application' };
        case 'completed':
          return { id, name: 'TA Completed', description: 'Paid and fulfilled' };
        default:
          return { id, name: 'TA Scenario', description: '' };
      }
    });
  }

  get selectedClient() {
    return getDemoData(this.selectedClientId);
  }

  get selectedScenario() {
    return getDemoScenario(this.selectedScenarioId);
  }

  get selectedClientName() {
    return this.selectedClient.client.name;
  }

  get selectedClientIndustry() {
    return this.selectedClient.client.industry;
  }

  get selectedScenarioName() {
    return this.selectedScenario.name;
  }

  get selectedTasScenarioName() {
    const option = this.tasScenarioOptions.find((item) => item.id === this.selectedTasScenarioId);
    return option?.name ?? 'TA Scenario';
  }

  get selectedTasScenarioDescription() {
    const option = this.tasScenarioOptions.find((item) => item.id === this.selectedTasScenarioId);
    return option?.description ?? '';
  }

  get selectedScenarioDescription() {
    return this.selectedScenario.description;
  }

  selectClient = (clientId: string) => {
    // Use full page reload to ensure all demo data refreshes
    setDemoClientId(clientId as DemoClientId);
  };

  selectScenario = (scenarioId: string) => {
    // Use full page reload to ensure all demo data refreshes
    setDemoScenarioId(scenarioId as DemoScenarioId);
  };

  selectTasScenario = (scenarioId: string) => {
    // Use full page reload to ensure all demo data refreshes
    setDemoTasScenarioId(scenarioId as DemoTasScenarioId);
  };

  toggleClientMenu = () => {
    this.isClientMenuOpen = !this.isClientMenuOpen;
    this.isScenarioMenuOpen = false;
    this.isTasScenarioMenuOpen = false;
  };

  toggleScenarioMenu = () => {
    this.isScenarioMenuOpen = !this.isScenarioMenuOpen;
    this.isClientMenuOpen = false;
    this.isTasScenarioMenuOpen = false;
  };

  toggleTasScenarioMenu = () => {
    this.isTasScenarioMenuOpen = !this.isTasScenarioMenuOpen;
    this.isClientMenuOpen = false;
    this.isScenarioMenuOpen = false;
  };

  closeClientMenu = () => {
    this.isClientMenuOpen = false;
  };

  closeScenarioMenu = () => {
    this.isScenarioMenuOpen = false;
  };

  closeTasScenarioMenu = () => {
    this.isTasScenarioMenuOpen = false;
  };

  toggleLegacyFrame = () => {
    this.isLegacyFrameHidden = !this.isLegacyFrameHidden;
    this.applyLegacyFrameState(this.isLegacyFrameHidden);
    // Persist to localStorage for page refresh persistence
    localStorage.setItem('reimagined-legacy-frame-hidden', String(this.isLegacyFrameHidden));
  };

  toggleDarkMode = () => {
    this.isDarkMode = !this.isDarkMode;
    // Toggle dark class on html element for CSS variable inheritance
    document.documentElement.classList.toggle('dark', this.isDarkMode);
  };

  // Data audit state is managed by reimaginedData service for reactivity across components

  getIsDemoMode() {
    return isReimaginedDemoMode();
  }

  toggleDemoMode = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    if (this.isDemoMode) {
      url.searchParams.delete('demo');
      url.searchParams.delete('demo_client');
      url.searchParams.delete('demo_product');
      url.searchParams.delete('demo_role');
      url.searchParams.delete('demo_scenario');
      url.searchParams.delete('demo_user_scenario');
      url.searchParams.delete('demo_ta_payment_model');
      url.searchParams.delete('demo_ta_scenario');
      url.searchParams.delete('demo_pslf_scenario');
      // Clear sticky demo state from localStorage
      localStorage.removeItem('reimagined-demo-mode');
      localStorage.removeItem('reimagined-demo-client');
      localStorage.removeItem('reimagined-demo-product');
      localStorage.removeItem('reimagined-demo-role');
      localStorage.removeItem('reimagined-demo-scenario');
      localStorage.removeItem('reimagined-demo-user-scenario');
      localStorage.removeItem('reimagined-demo-ta-payment-model');
      localStorage.removeItem('reimagined-demo-ta-scenario');
      localStorage.removeItem('reimagined-demo-pslf-scenario');
    } else {
      url.searchParams.set('demo', 'true');
      url.searchParams.set('demo_client', this.selectedClientId);
      url.searchParams.set('demo_scenario', this.selectedScenarioId);
      url.searchParams.set('demo_ta_scenario', this.selectedTasScenarioId);
    }

    window.location.assign(url.toString());
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="min-h-screen overflow-hidden tio-bg-surface isolate">
      <Header class="relative z-30 border-b py-4 shrink-0 tio-border-default">
        <div class="w-full {{if this.isLegacyFrameHidden 'px-4' 'max-w-7xl mx-auto px-4'}}">
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <PhPath @size={{32}} class="tio-fg-brand" />
              <span class="text-2xl font-bold tio-fg-primary">TIO Reimagined</span>
              <span class="ml-2 tio-demo-badge">DEMO</span>
            </div>

            {{! Demo scenario selectors }}
            <div class="flex items-center gap-3">
              <ScenarioPicker
                @intent="brand"
                @label="Demo Client"
                @title={{this.selectedClientName}}
                @subtitle={{this.selectedClientIndustry}}
                @icon={{PhBuildings}}
                @options={{this.clientOptions}}
                @selectedId={{this.selectedClientId}}
                @isOpen={{this.isClientMenuOpen}}
                @onToggle={{this.toggleClientMenu}}
                @onClose={{this.closeClientMenu}}
                @onSelect={{this.selectClient}}
                @showClientMeta={{true}}
              />

              <ScenarioPicker
                @intent="success"
                @label="User Scenario"
                @title={{this.selectedScenarioName}}
                @subtitle="User Scenario"
                @icon={{PhUserCircle}}
                @options={{this.scenarioOptions}}
                @selectedId={{this.selectedScenarioId}}
                @isOpen={{this.isScenarioMenuOpen}}
                @onToggle={{this.toggleScenarioMenu}}
                @onClose={{this.closeScenarioMenu}}
                @onSelect={{this.selectScenario}}
              />

              <ScenarioPicker
                @intent="warning"
                @label="TA Scenario"
                @title={{this.selectedTasScenarioName}}
                @subtitle="TA Scenario"
                @icon={{PhGraduationCap}}
                @options={{this.tasScenarioOptions}}
                @selectedId={{this.selectedTasScenarioId}}
                @isOpen={{this.isTasScenarioMenuOpen}}
                @onToggle={{this.toggleTasScenarioMenu}}
                @onClose={{this.closeTasScenarioMenu}}
                @onSelect={{this.selectTasScenario}}
              />

              {{! Demo Control Buttons }}
              <div class="flex items-center gap-2 pl-3 border-l tio-border-strong">
                {{! Hide Legacy Frame Toggle }}
                <button
                  type="button"
                  class="tio-demo-toggle
                    {{if
                      this.isLegacyFrameHidden
                      'tio-demo-toggle--active tio-demo-toggle--warning'
                    }}"
                  title="{{if this.isLegacyFrameHidden 'Show legacy frame' 'Hide legacy frame'}}"
                  {{on "click" this.toggleLegacyFrame}}
                >
                  {{#if this.isLegacyFrameHidden}}
                    <PhEye @size={{20}} />
                  {{else}}
                    <PhEyeSlash @size={{20}} />
                  {{/if}}
                </button>

                {{! Demo Mode Toggle }}
                <button
                  type="button"
                  class="tio-demo-toggle
                    {{if this.isDemoMode 'tio-demo-toggle--active tio-demo-toggle--brand'}}"
                  title="{{if this.isDemoMode 'Disable demo mode' 'Enable demo mode'}}"
                  {{on "click" this.toggleDemoMode}}
                >
                  <PhSparkle @size={{20}} />
                </button>

                {{! Data Audit Toggle (rose at the legacy call site → danger) }}
                <button
                  type="button"
                  class="tio-demo-toggle
                    {{if
                      this.reimaginedData.isDataAuditEnabled
                      'tio-demo-toggle--active tio-demo-toggle--danger'
                    }}"
                  title="{{if
                    this.reimaginedData.isDataAuditEnabled
                    'Disable data audit'
                    'Enable data audit'
                  }}"
                  {{on "click" this.reimaginedData.toggleDataAudit}}
                >
                  <PhWarning @size={{20}} />
                </button>

                {{! Component Review Toggle }}
                <button
                  type="button"
                  class="tio-demo-toggle
                    {{if
                      this.componentReview.isReviewModeEnabled
                      'tio-demo-toggle--active tio-demo-toggle--brand'
                    }}"
                  title="{{if
                    this.componentReview.isReviewModeEnabled
                    'Disable component review'
                    'Enable component review'
                  }}"
                  {{on "click" this.componentReview.toggleReviewMode}}
                >
                  <PhChatCircleDots @size={{20}} />
                </button>

                {{! Dark Mode Toggle (indigo at the legacy call site → brand) }}
                <button
                  type="button"
                  class="tio-demo-toggle
                    {{if this.isDarkMode 'tio-demo-toggle--active tio-demo-toggle--brand'}}"
                  title="{{if this.isDarkMode 'Switch to light mode' 'Switch to dark mode'}}"
                  {{on "click" this.toggleDarkMode}}
                >
                  {{#if this.isDarkMode}}
                    <PhSun @size={{20}} />
                  {{else}}
                    <PhMoon @size={{20}} />
                  {{/if}}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Header>

      {{! Reimagined Navigation - Mega-menu with dropdowns }}
      <MegaMenuNav
        @scenarioId={{this.selectedScenarioId}}
        @isFullscreen={{this.isLegacyFrameHidden}}
      />

      {{! Main content area - constrained width with proper overflow }}
      <div class="flex-1 overflow-y-auto overflow-x-hidden min-w-0 tio-bg-surface">
        <div
          class="w-full pt-2 pb-6 {{if this.isLegacyFrameHidden 'px-4' 'max-w-7xl mx-auto px-4'}}"
        >
          {{outlet}}
        </div>
      </div>

      {{! Data Source Documentation Panel - dev only }}
      <DataSourceDocs
        @isOpen={{this.reimaginedData.isDocsOpen}}
        @onClose={{this.reimaginedData.closeDocs}}
        @sectionId={{this.reimaginedData.docsSectionId}}
      />

      {{! Component Review Thread Panel }}
      <ReviewDrawer
        @isOpen={{this.componentReview.isDrawerOpen}}
        @onClose={{this.componentReview.closeDrawer}}
        @reviewId={{this.componentReview.activeReviewId}}
        @pagePath={{this.componentReview.activePagePath}}
      />

      {{! Reimagined Toast Notifications }}
      <ToastContainer @toasts={{this.toast.toasts}} @onDismiss={{this.toast.dismiss}} />
    </VStack>
  </template>
}
